import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "studentCount", "staffCount",
    "studentCoeff", "staffCoeff", "tuitionCoeff",
    "formulaBreakdown",
    "familyPrice",
    "engagementYears", "engagementDiscount",
    // School pays summary
    "corePrice", "modulesPrice", "subtotal",
    "discount", "annualTotal", "engagementTotal",
    "modulesPriceRow",
    // Parents pay summary
    "familyRevenue", "schoolFee", "schoolFeeHighlight", "schoolFeeOverride", "parentsSubtotal",
    "parentsDiscount", "parentsAnnualTotal", "parentsEngagementTotal",
    // Summary panels
    "schoolPaysSummary", "parentsPaySummary",
    // Drive storage
    "driveStorage",
    // KPI
    "kpiCostPerStudent", "kpiCostPerMonth",
    "kpiCostPerUser", "kpiTuitionPct",
    // Visibility
    "prospectFields", "existingFields",
    "optionSection", "bothOption", "pricingOptionHidden",
    "schoolPaysSection", "coefficientsSection",
    "familySection", "tuitionField",
    "moduleAutoPrice",
    "modeHint",
    // Auto plan selection
    "planName",
    // Family price breakdown
    "familyPriceDisplay",
    // Drive tiers table
    "driveTiersBody",
    // Engagement cards
    "engagementCard",
    // VAT
    "vatRate", "vatAmount", "totalWithVat",
  ]

  static values = {
    coreTiers: Array,
    payrollTiers: Array,
    optionalTiers: Array,
    engagementDiscounts: Array,
    studentCoefficient: Number,
    staffCoefficient: Number,
    tuitionCoefficient: Number,
    annualFamilyPrice: Number,
    symbolicTiers: Array,
    driveTiers: Array,
    allPlans: Array,
    currentPlanId: Number,
  }

  static modeHints = {
    fr: {
      multiplicative: "Prix de base du palier \u00d7 coefficient \u00e9l\u00e8ves",
      additive: "Prix de base du palier + (nombre d\u2019\u00e9l\u00e8ves \u00d7 coefficient \u00e9l\u00e8ves)",
    },
    en: {
      multiplicative: "Tier base price \u00d7 student coefficient",
      additive: "Tier base price + (student count \u00d7 student coefficient)",
    },
  }

  connect() {
    this._prefillModulePrices()
    this.calculate()
  }

  updateModeHint() {
    if (!this.hasModeHintTarget) return
    const mode = this.element.querySelector(
      'input[name="pricing_simulation[calculation_mode]"]:checked'
    )?.value || "multiplicative"
    const lang = document.documentElement.lang || "fr"
    const hints = this.constructor.modeHints[lang] ||
      this.constructor.modeHints.fr
    this.modeHintTarget.textContent = hints[mode] || ""
    this.calculate()
  }

  async loadPlanData(event) {
    const planId = event.target.value
    if (!planId) return

    try {
      const locale = document.documentElement.lang || 'fr'
      const url = `/${locale}/admin/pricing_simulations/plan_data?plan_id=${planId}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
      })
      if (!response.ok) return
      const data = await response.json()

      this.coreTiersValue = data.core_tiers || []
      this.payrollTiersValue = data.payroll_tiers || []
      this.optionalTiersValue = data.optional_module_tiers || []
      this.engagementDiscountsValue = data.engagement_discounts || []
      this.studentCoefficientValue = data.student_coefficient || 1
      this.staffCoefficientValue = data.staff_coefficient || 1
      this.tuitionCoefficientValue = data.tuition_coefficient || 0.01
      this.annualFamilyPriceValue = data.annual_family_price || 0
      this.symbolicTiersValue = data.symbolic_tiers || []
      this.driveTiersValue = data.drive_tiers || []

      if (this.hasStudentCoeffTarget) {
        this.studentCoeffTarget.value = data.student_coefficient || 1
      }
      if (this.hasStaffCoeffTarget) {
        this.staffCoeffTarget.value = data.staff_coefficient || 1
      }
      if (this.hasFamilyPriceTarget) {
        this.familyPriceTarget.value = data.annual_family_price || 0
      }

      const currencyInput = this.element.querySelector(
        'input[name="pricing_simulation[currency]"]'
      )
      if (currencyInput && data.currency) {
        currencyInput.value = data.currency
      }

      this.element.querySelectorAll(
        'input[name$="[overridden_price]"]'
      ).forEach(input => { input.value = '' })

      this._prefillModulePrices()
      this.calculate()
    } catch (_) {
      // silently ignore fetch errors
    }
  }

  toggleScenario() {
    const scenario = this.element.querySelector(
      'input[name="pricing_simulation[scenario_type]"]:checked'
    )?.value

    if (this.hasProspectFieldsTarget) {
      this.prospectFieldsTarget.classList.toggle("hidden", scenario !== "prospect")
    }
    if (this.hasExistingFieldsTarget) {
      this.existingFieldsTarget.classList.toggle("hidden", scenario !== "existing")
    }

    const isProspect = scenario === "prospect"
    const isExisting = scenario === "existing"

    // L'option se choisit pour le prospect comme pour l'école existante.
    if (this.hasOptionSectionTarget) {
      this.optionSectionTarget.classList.toggle("hidden", !(isProspect || isExisting))
    }

    // "Les deux" est réservé au prospect : on le masque pour l'école existante
    // et, si elle était cochée, on retombe sur "L'école paie".
    if (this.hasBothOptionTarget) {
      this.bothOptionTarget.classList.toggle("hidden", !isProspect)
    }
    const bothRadio = this.element.querySelector(
      'input[name="_pricing_option_ui"][value="both"]'
    )
    if (!isProspect && bothRadio?.checked) {
      const schoolRadio = this.element.querySelector(
        'input[name="_pricing_option_ui"][value="school_pays"]'
      )
      if (schoolRadio) schoolRadio.checked = true
    }

    this._syncOptionFromRadio()
    this._toggleSummaryPanels()
    this.calculate()
  }

  toggleOption() {
    this._syncOptionFromRadio()
    this._toggleSummaryPanels()
    this.calculate()
  }

  // Le nombre d'élèves change → on recalcule instantanément.
  onStudentCountChange() {
    this.calculate()
  }

  loadSchoolData(event) {
    const schoolId = event.target.value
    if (!schoolId) return

    const locale = document.documentElement.lang || 'fr'
    fetch(`/${locale}/admin/pricing_simulations/school_data?school_id=${schoolId}`, {
      headers: { "Accept": "application/json" }
    })
      .then(r => r.json())
      .then(data => {
        if (this.hasStudentCountTarget) this.studentCountTarget.value = data.student_count
        if (this.hasStaffCountTarget) this.staffCountTarget.value = data.staff_count
        this._prefillModulePrices()
        this.calculate()
      })
  }

  selectEngagement(event) {
    const card = event.currentTarget
    const years = card.dataset.years
    const discount = card.dataset.discount

    if (this.hasEngagementYearsTarget) {
      this.engagementYearsTarget.value = years
    }
    if (this.hasEngagementDiscountTarget) {
      this.engagementDiscountTarget.value = discount
    }

    this.engagementCardTargets.forEach(c => {
      c.classList.remove(
        "border-cta", "bg-cta-shared", "ring-2", "ring-cta"
      )
      c.classList.add("border-gray-200")
    })
    card.classList.remove("border-gray-200")
    card.classList.add(
      "border-cta", "bg-cta-shared", "ring-2", "ring-cta"
    )

    this.calculate()
  }

  calculate() {
    this._autoSelectPlan()
    this._updateModuleAutoPrices()

    const option = this._currentOption()

    if (option === "both") {
      this._calculateSchoolPays()
      this._calculateParentsPay()
    } else if (option === "school_pays") {
      this._calculateSchoolPays()
    } else {
      this._calculateParentsPay()
    }

    this._updateDriveInfo()
    this._updateKPIs()
  }

  // Private

  _autoSelectPlan() {
    const plans = this.allPlansValue
    if (!plans || !plans.length) return

    const students = this._intVal(this.studentCountTarget)
    if (students <= 0) return

    for (const plan of plans) {
      const tiers = plan.core_tiers || []
      const match = tiers.find(t => {
        const min = parseInt(t.min_students) || 0
        const max = t.max_students
        return students >= min &&
          (!max || max === '' || students <= parseInt(max))
      })
      if (!match) continue

      if (plan.id === this.currentPlanIdValue) return

      // Switch to the matching plan
      this.currentPlanIdValue = plan.id
      this.coreTiersValue = plan.core_tiers || []
      this.payrollTiersValue = plan.payroll_tiers || []
      this.optionalTiersValue = plan.optional_module_tiers || []
      this.engagementDiscountsValue = plan.engagement_discounts || []
      this.studentCoefficientValue =
        parseFloat(plan.student_coefficient) || 1
      this.staffCoefficientValue =
        parseFloat(plan.staff_coefficient) || 1
      this.tuitionCoefficientValue =
        parseFloat(plan.tuition_coefficient) || 0.01
      this.annualFamilyPriceValue =
        parseFloat(plan.annual_family_price) || 0
      this.symbolicTiersValue = plan.symbolic_tiers || []
      this.driveTiersValue = plan.drive_tiers || []

      // Update plan name badge
      if (this.hasPlanNameTarget) {
        this.planNameTarget.textContent = plan.name
      }
      return
    }
  }

  _currentOption() {
    if (this.hasPricingOptionHiddenTarget) {
      return this.pricingOptionHiddenTarget.value
    }
    return "school_pays"
  }

  _setPricingOption(value) {
    if (this.hasPricingOptionHiddenTarget) {
      this.pricingOptionHiddenTarget.value = value
    }
  }

  _syncOptionFromRadio() {
    const option = this.element.querySelector(
      'input[name="_pricing_option_ui"]:checked'
    )?.value || "school_pays"

    this._setPricingOption(option)

    // "both" affiche les deux familles de sections (école + parents).
    if (option === "both") {
      this._showAllSections()
      return
    }

    const isSchool = option === "school_pays"
    this.schoolPaysSectionTargets.forEach(el => el.classList.toggle("hidden", !isSchool))
    if (this.hasCoefficientsSectionTarget) {
      this.coefficientsSectionTarget.classList.toggle("hidden", !isSchool)
    }
    if (this.hasFamilySectionTarget) {
      this.familySectionTarget.classList.toggle("hidden", isSchool)
    }
    if (this.hasTuitionFieldTarget) {
      this.tuitionFieldTarget.classList.toggle("hidden", !isSchool)
    }
  }

  _showAllSections() {
    this.schoolPaysSectionTargets.forEach(el => el.classList.remove("hidden"))
    if (this.hasCoefficientsSectionTarget) {
      this.coefficientsSectionTarget.classList.remove("hidden")
    }
    if (this.hasFamilySectionTarget) {
      this.familySectionTarget.classList.remove("hidden")
    }
    if (this.hasTuitionFieldTarget) {
      this.tuitionFieldTarget.classList.remove("hidden")
    }
  }

  _toggleSummaryPanels() {
    const option = this._currentOption()
    const isBoth = option === "both"
    const isSchool = option === "school_pays"
    const isParents = option === "parents_pay"

    if (this.hasSchoolPaysSummaryTarget) {
      this.schoolPaysSummaryTarget.classList.toggle("hidden", isParents)
    }
    if (this.hasParentsPaySummaryTarget) {
      this.parentsPaySummaryTarget.classList.toggle("hidden", isSchool)
    }
  }

  onModulePriceInput(event) {
    delete event.target.dataset.autoFilled
    this.calculate()
  }

  _prefillModulePrices() {
    const students = this._intVal(this.studentCountTarget)
    const staff = this._intVal(this.staffCountTarget)

    this.element.querySelectorAll('[data-module-name]').forEach(row => {
      const input = row.querySelector('input[name$="[overridden_price]"]')
      if (!input) return

      // Inputs that already hold a value are saved/manual overrides: leave
      // them untouched and do NOT flag them auto-filled, otherwise
      // _updateModuleAutoPrices would clobber them with the tier price.
      if (input.value) return

      const modName = row.dataset.moduleName
      const price = this._autoModulePrice(modName, students, staff)
      if (price > 0) input.value = price
      // Only inputs we auto-filled should recalculate when the
      // student/staff count changes.
      input.dataset.autoFilled = "true"
    })
  }

  _updateModuleAutoPrices() {
    const students = this._intVal(this.studentCountTarget)
    const staff = this._intVal(this.staffCountTarget)
    const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA"

    this.moduleAutoPriceTargets.forEach(span => {
      const modName = span.dataset.moduleName
      // The span carries its own data-module-name, so search from the parent
      // to reach the row that holds the override input / data attribute.
      const row = span.parentElement?.closest("[data-module-name]")
      const input = row?.querySelector('input[name$="[overridden_price]"]')

      // A saved override (rendered as a data attribute on read-only surfaces
      // like the public simulator) is a fixed price: use it instead of the
      // tier auto-price so admin-edited prices are reflected.
      const savedOverride = this._rowOverride(row)
      const autoPrice = savedOverride !== null
        ? savedOverride
        : this._autoModulePrice(modName, students, staff)

      // Update input if empty or was auto-filled (not manually edited)
      if (input && autoPrice > 0 &&
          (!input.value || input.dataset.autoFilled === "true")) {
        input.value = autoPrice
        input.dataset.autoFilled = "true"
      }

      // Only show auto price label when the input is empty
      if (input && input.value) {
        span.textContent = ""
      } else {
        span.textContent = autoPrice > 0 ? fmt(autoPrice) : ""
      }
    })
  }

  // Saved per-module override price exposed via data-module-override
  // (read-only surfaces such as the public simulator). Returns null when
  // absent, blank or non-positive so callers fall back to the tier price.
  _rowOverride(row) {
    if (!row) return null
    const raw = row.dataset.moduleOverride
    if (raw === undefined || raw === "") return null
    const value = parseFloat(raw)
    return isNaN(value) || value <= 0 ? null : value
  }

  _autoModulePrice(modName, students, staff) {
    if (modName === "GO4SPAYROLL") {
      const tier = this._findTier(this.payrollTiersValue, staff, "min_staff", "max_staff")
      return tier ? parseFloat(tier.price) || 0 : 0
    } else {
      const total = staff + students
      const tier = this._findTier(this.optionalTiersValue, total, "min_total", "max_total")
      if (!tier) return 0
      const key = modName.toLowerCase()
      return parseFloat(tier[key]) || 0
    }
  }

  _calculateSchoolPays() {
    const students = this._intVal(this.studentCountTarget)
    const staff = this._intVal(this.staffCountTarget)
    const studentCoeff = this.hasStudentCoeffTarget
      ? this._floatVal(this.studentCoeffTarget)
      : this.studentCoefficientValue
    const staffCoeff = this.hasStaffCoeffTarget
      ? this._floatVal(this.staffCoeffTarget)
      : this.staffCoefficientValue
    const tuitionCoeff = this.hasTuitionCoeffTarget
      ? this._floatVal(this.tuitionCoeffTarget)
      : this.tuitionCoefficientValue
    const tuitionInput = this.element.querySelector(
      'input[name="pricing_simulation[average_tuition_amount]"]'
    )
    const avgTuition = tuitionInput
      ? parseFloat(tuitionInput.value) || 0
      : 0
    const mode = this.element.querySelector(
      'input[name="pricing_simulation[calculation_mode]"]:checked'
    )?.value || "multiplicative"

    const baseTier = this._findTier(this.coreTiersValue, students, "min_students", "max_students")
    const basePrice = baseTier ? parseFloat(baseTier.base_price) || 0 : 0
    let studentComponent
    if (mode === "multiplicative") {
      studentComponent = basePrice * studentCoeff
    } else {
      studentComponent = basePrice + (students * studentCoeff)
    }
    const staffComponent = staff * staffCoeff
    const tuitionComponent = students * avgTuition * tuitionCoeff
    const corePrice = studentComponent + staffComponent + tuitionComponent

    this._updateFormulaBreakdown({
      mode, baseTier, basePrice,
      studentCoeff, staffCoeff, tuitionCoeff,
      students, staff, avgTuition,
      studentComponent, staffComponent, tuitionComponent, corePrice,
    })

    let modulesPrice = 0
    this.element.querySelectorAll('input[name$="[included]"][type="checkbox"]').forEach(cb => {
      if (!cb.checked) return
      const row = cb.closest("[data-module-name]")
      const overrideInput = row?.querySelector('input[name$="[overridden_price]"]')
      const overrideVal = overrideInput ? parseFloat(overrideInput.value) : NaN

      const savedOverride = this._rowOverride(row)
      if (!isNaN(overrideVal) && overrideVal > 0) {
        modulesPrice += overrideVal
      } else if (savedOverride !== null) {
        modulesPrice += savedOverride
      } else {
        const modName = row?.dataset?.moduleName
        modulesPrice += this._autoModulePrice(modName || "", this._intVal(this.studentCountTarget), this._intVal(this.staffCountTarget))
      }
    })

    const subtotal = corePrice + modulesPrice
    const years = this._intVal(this.engagementYearsTarget) || 1
    const discPct = this._floatVal(this.engagementDiscountTarget) || 0
    const discount = subtotal * discPct / 100
    const annual = subtotal - discount
    const engagement = annual * years

    const vatRate = this.hasVatRateTarget
      ? this._floatVal(this.vatRateTarget)
      : 19.25
    const vatAmount = annual * vatRate / 100
    const totalWithVat = annual + vatAmount

    const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA"
    if (this.hasCorePriceTarget) this.corePriceTarget.textContent = fmt(corePrice)
    if (this.hasModulesPriceTarget) this.modulesPriceTarget.textContent = fmt(modulesPrice)
    if (this.hasSubtotalTarget) this.subtotalTarget.textContent = fmt(subtotal)
    if (this.hasDiscountTarget) this.discountTarget.textContent = "-" + fmt(discount)
    if (this.hasAnnualTotalTarget) this.annualTotalTarget.textContent = fmt(annual)
    if (this.hasVatAmountTarget) this.vatAmountTarget.textContent = fmt(vatAmount)
    if (this.hasTotalWithVatTarget) this.totalWithVatTarget.textContent = fmt(totalWithVat)
    if (this.hasEngagementTotalTarget) this.engagementTotalTarget.textContent = fmt(engagement)
  }

  _updateFormulaBreakdown({
    mode, baseTier, basePrice,
    studentCoeff, staffCoeff, tuitionCoeff,
    students, staff, avgTuition,
    studentComponent, staffComponent, tuitionComponent, corePrice,
  }) {
    if (!this.hasFormulaBreakdownTarget) return

    const lang = document.documentElement.lang || "fr"
    const isFr = lang !== "en"
    const fmtN = (n) =>
      new Intl.NumberFormat("fr-FR").format(Math.round(n))
    const fmtC = (n) => fmtN(n) + " FCFA"

    const tierName = baseTier?.name ||
      (isFr ? "Aucun palier" : "No tier")
    const tierMin = baseTier?.min_students || 0
    const tierMax = baseTier?.max_students
    const tierRange = tierMax
      ? `${fmtN(tierMin)}–${fmtN(tierMax)} ${isFr ? "él." : "st."}`
      : `≥ ${fmtN(tierMin)} ${isFr ? "él." : "st."}`

    const modeName = mode === "multiplicative"
      ? (isFr ? "Multiplicatif" : "Multiplicative")
      : (isFr ? "Additif" : "Additive")

    const studentExpr = mode === "multiplicative"
      ? `${fmtN(basePrice)} × ${studentCoeff}`
      : `${fmtN(basePrice)} + (${fmtN(students)} × ${studentCoeff})`

    const rows = [
      [
        isFr ? `Palier (${tierName})` : `Tier (${tierName})`,
        studentExpr,
        fmtC(studentComponent),
      ],
      [
        `+ ${isFr ? "Personnel" : "Staff"}`,
        `${fmtN(staff)} × ${staffCoeff}`,
        fmtC(staffComponent),
      ],
      [
        `+ ${isFr ? "Scolarité" : "Tuition"}`,
        `${fmtN(students)} × ${fmtN(avgTuition)} × ${tuitionCoeff}`,
        fmtC(tuitionComponent),
      ],
    ]

    const header = isFr
      ? `Mode <strong>${modeName}</strong> — Palier <strong>${tierName}</strong> (${tierRange})`
      : `<strong>${modeName}</strong> mode — Tier <strong>${tierName}</strong> (${tierRange})`

    let html =
      `<p class="text-xs font-semibold text-indigo-700 mb-2">${header}</p>` +
      `<div class="space-y-1">`
    for (const [label, expr, result] of rows) {
      html +=
        `<div class="flex items-baseline gap-2 text-xs">` +
        `<span class="w-32 shrink-0 text-gray-400">${label}</span>` +
        `<span class="flex-1 font-mono text-gray-500">${expr}</span>` +
        `<span class="font-mono font-medium text-gray-700 whitespace-nowrap">${result}</span>` +
        `</div>`
    }
    html +=
      `<div class="flex items-baseline gap-2 text-xs border-t border-indigo-200 pt-1 mt-1">` +
      `<span class="w-32 shrink-0 font-semibold text-indigo-700">GO4SCORE</span>` +
      `<span class="flex-1"></span>` +
      `<span class="font-mono font-bold text-indigo-700 whitespace-nowrap">${fmtC(corePrice)}</span>` +
      `</div>` +
      `</div>`

    this.formulaBreakdownTarget.innerHTML = html
  }

  _calculateParentsPay() {
    const familyPrice = this.hasFamilyPriceTarget
      ? this._floatVal(this.familyPriceTarget)
      : this.annualFamilyPriceValue
    const studentCount = this._intVal(this.studentCountTarget)

    const fmtN = (n) =>
      new Intl.NumberFormat("fr-FR").format(Math.round(n))
    if (this.hasFamilyPriceDisplayTarget) {
      this.familyPriceDisplayTarget.textContent =
        fmtN(familyPrice) + " FCFA"
    }
    // Le revenu familles est désormais simplement le prix annuel par élève
    // multiplié par le nombre d'élèves.
    const familyRevenue = familyPrice * studentCount

    const total = this._intVal(this.studentCountTarget) + this._intVal(this.staffCountTarget)
    const symbolicTier = this._findTier(this.symbolicTiersValue, total, "min_total", "max_total")
    const tieredFee = symbolicTier ? parseFloat(symbolicTier.price) || 0 : 0

    let schoolFee = tieredFee
    if (this.hasSchoolFeeOverrideTarget) {
      const ov = this.schoolFeeOverrideTarget
      const tieredRounded = Math.round(tieredFee)
      const isAutoValue = ov.value === "" ||
        (this._schoolFeeAutoFilled !== undefined &&
         parseFloat(ov.value) === this._schoolFeeAutoFilled)
      if (isAutoValue) {
        ov.value = tieredRounded
        this._schoolFeeAutoFilled = tieredRounded
      }
      schoolFee = parseFloat(ov.value) || 0
    }
    const annual = familyRevenue + schoolFee

    const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA"
    if (this.hasFamilyRevenueTarget) this.familyRevenueTarget.textContent = fmt(familyRevenue)
    if (this.hasSchoolFeeTarget) this.schoolFeeTarget.textContent = fmt(schoolFee)
    if (this.hasSchoolFeeHighlightTarget) {
      this.schoolFeeHighlightTarget.textContent = fmt(schoolFee)
    }
    if (this.hasParentsAnnualTotalTarget) this.parentsAnnualTotalTarget.textContent = fmt(annual)
  }

  _updateKPIs() {
    const students = this._intVal(this.studentCountTarget)
    if (students <= 0) return

    const staff = this._intVal(this.staffCountTarget)
    const annualEl = this.hasAnnualTotalTarget
      ? this.annualTotalTarget
      : this.hasParentsAnnualTotalTarget
        ? this.parentsAnnualTotalTarget
        : null
    if (!annualEl) return

    const annualText = annualEl.textContent.replace(/[^\d]/g, "")
    const annual = parseInt(annualText) || 0

    const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n))
    if (this.hasKpiCostPerStudentTarget) {
      this.kpiCostPerStudentTarget.textContent = fmt(annual / students) + " FCFA"
    }
    if (this.hasKpiCostPerMonthTarget) {
      this.kpiCostPerMonthTarget.textContent = fmt(annual / students / 10) + " FCFA"
    }
    if (this.hasKpiCostPerUserTarget) {
      const users = students + staff
      this.kpiCostPerUserTarget.textContent = users > 0 ? fmt(annual / users) + " FCFA" : "N/A"
    }
    if (this.hasKpiTuitionPctTarget) {
      const tuitionInput = this.element.querySelector('input[name="pricing_simulation[average_tuition_amount]"]')
      const tuition = tuitionInput ? parseFloat(tuitionInput.value) || 0 : 0
      if (tuition > 0 && students > 0) {
        const pct = (annual / (students * tuition) * 100).toFixed(1)
        this.kpiTuitionPctTarget.textContent = pct + "%"
      } else {
        this.kpiTuitionPctTarget.textContent = "N/A"
      }
    }
  }

  _updateDriveInfo() {
    const total = this._intVal(this.studentCountTarget) +
      this._intVal(this.staffCountTarget)

    if (this.hasDriveStorageTarget) {
      const tier = this._findTier(
        this.driveTiersValue, total,
        "min_total", "max_total",
      )
      this.driveStorageTarget.textContent =
        tier ? tier.storage_included : "-"
    }

    if (this.hasDriveTiersBodyTarget) {
      this._rebuildDriveTiersTable()
    }
  }

  _rebuildDriveTiersTable() {
    const tiers = this.driveTiersValue
    if (!tiers || !tiers.length) return

    this.driveTiersBodyTarget.innerHTML = ""
    for (const tier of tiers) {
      const max = tier.max_total || "\u221E"
      const tr = document.createElement("tr")

      const tdName = document.createElement("td")
      tdName.className = "px-4 py-2 text-gray-700"
      tdName.textContent = tier.name
      tr.appendChild(tdName)

      const tdRange = document.createElement("td")
      tdRange.className = "px-4 py-2 text-center text-gray-500"
      tdRange.textContent = `${tier.min_total} - ${max}`
      tr.appendChild(tdRange)

      const tdStorage = document.createElement("td")
      tdStorage.className = "px-4 py-2 text-right font-medium text-gray-900"
      tdStorage.textContent = tier.storage_included
      tr.appendChild(tdStorage)

      this.driveTiersBodyTarget.appendChild(tr)
    }
  }

  _findTier(tiers, value, minKey, maxKey) {
    if (!tiers || !tiers.length || value <= 0) return null
    return tiers.find(t => {
      const min = parseInt(t[minKey]) || 0
      const max = t[maxKey]
      return value >= min && (!max || max === "" || value <= parseInt(max))
    })
  }

  _intVal(target) {
    if (!target) return 0
    return parseInt(target.value) || 0
  }

  _floatVal(target) {
    if (!target) return 0
    return parseFloat(target.value) || 0
  }
}
